import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Container, List, ListItem, ListItemIcon, Badge,
    ListItemText, Collapse, Paper, Grid, Divider, Snackbar, Alert, Tooltip,
    Drawer, IconButton, useMediaQuery, Chip,
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InboxIcon from '@mui/icons-material/Inbox';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupportIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PolicyIcon from '@mui/icons-material/Policy';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutIcon from '@mui/icons-material/Logout';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PostLoads from './PostLoads';
import SearchTrucks from './SearchTrucks';
import LoadHistory from './LoadHistory';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FeedbackIcon from '@mui/icons-material/Feedback';
import Directory from './Directory';
import Notifications from './Notifications';
import UserProfile from './UserProfile';
import DealDesk from './DealDesk';
import SD_logo from '../assests/shipperdock.png';
import UpdatesAndTipsViewer from './UpdatesAndTipsViewer';
import TrainingViewer from './TrainingViewer';
import TemplatesViewer from './TemplatesViewer';
import FeedbackDialog from './FeedbackDialog';
import MarketMap from './MarketMap';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import SupportForum from './SupportForum';
import HelpCenter from './HelpCenter';
import CompanyProfile from './CompanyProfile';
import LiveChat from './LiveChat';
import axios from 'axios';
import PendingApproval from './PendingApproval';
import CircularProgress from '@mui/material/CircularProgress';
import SearchCarriers from './SearchCarriers';
import FavoritesViewer from './FavoritesViewer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; // already imported in some dashboards
import StarIcon from '@mui/icons-material/Star';
import SearchShippers from './SearchShippers';


// --- Styling and Theme ---
const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#0e1d3a' },
        warning: { main: '#ffb300' },
    },
    typography: { fontFamily: 'Inter, Arial, sans-serif' },
});

const drawerWidth = 240;
const collapsedWidth = 70;
const dashboardHeaderHeight = '44px';

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    minHeight: '100vh',
}));

const Sidebar = styled(Box)(({ theme, collapsed }) => ({
    width: collapsed ? collapsedWidth : drawerWidth,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    background: 'linear-gradient(to bottom, #0e1d3a, #11224d)',
    color: 'white',
    boxShadow: '2px 0 5px rgba(0, 0, 0, 0.2)',
    position: 'fixed',
    height: '100dvh',
    zIndex: 1200,
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const SidebarItem = styled(ListItem)(({ theme, collapsed }) => ({
    cursor: 'pointer',
    justifyContent: collapsed ? 'center' : 'flex-start',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-selected': {
        backgroundColor: collapsed ? 'rgba(77, 157, 224, 0.3)' : 'rgba(255, 255, 255, 0.2)',
        borderLeft: collapsed ? 'none' : '4px solid #4D9DE0',
    },
    position: 'relative',
}));

const NestedSidebarItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingLeft: theme.spacing(2),
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
}));

const StyledCollapse = styled(Collapse)(({ theme }) => ({
    zIndex: 1300,
}));

// ── CARRIER-STYLE HEADER ──
const DashboardHeader = styled(Box)(({ theme, collapsed }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0',
    background: 'linear-gradient(135deg, #facc15 0%, #f59e0b 100%)',
    backdropFilter: 'blur(10px)',
    borderBottom: '3px solid #dfa419',
    borderTop: '2px solid rgba(255,255,255,0.3)',
    position: 'fixed',
    top: 0,
    zIndex: 1099,
    height: '44px',
    boxSizing: 'border-box',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    transition: theme.transitions.create(['width', 'left'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
}));

const NavTab = styled(Box)(({ active, isDealDesk }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px', // Matched to PageNameTag gap
    padding: '0 20px', // Matched to PageNameTag padding
    height: '100%',
    alignSelf: 'stretch',
    cursor: 'pointer',
    position: 'relative',
    fontSize: '0.68rem', // Matched to PageNameTag label size
    fontWeight: 800, // Matched to PageNameTag label weight
    fontFamily: "'Inter', sans-serif",
    textTransform: 'uppercase', // Matched to PageNameTag
    letterSpacing: '0.9px', // Matched to PageNameTag
    color: active ? '#0f172a' : 'rgba(0,0,0,0.7)',
    background: active ? '#ffffff' : 'transparent',
    borderRight: '2px solid rgba(0,0,0,0.13)',
    borderBottom: active ? '3px solid #000000' : '3px solid transparent',
    transition: 'all 0.2s ease',

    '&:hover': {
        background: '#0e1d3a', // Dark hover background like PageNameTag
        color: '#ffffff', // Text turns white on hover
        '& .MuiSvgIcon-root': {
            color: '#facc15', // Icon turns yellow like the dot
            transform: 'scale(1.05)',
        },
    },

    '& .MuiSvgIcon-root': {
        fontSize: '0.9rem',
        fontWeight: 900,
        color: active ? '#0f172a' : 'rgba(0,0,0,0.65)',
        transition: '0.2s',
    },

    '&:active': {
        transform: 'scale(0.97)',
    },
}));
const LogoutTab = styled(NavTab)(() => ({
    color: '#ffffff', // Red color
    borderRight: 'none',
    borderLeft: '2px solid rgba(0,0,0,0.13)',
    '& .MuiSvgIcon-root': {
        color: '#ffffff',
    },
    '&:hover': {
        background: '#dc2626', // Turns red on hover
        color: '#ffffff',
        '& .MuiSvgIcon-root': {
            color: '#ffffff', // Icon turns white on hover
        },
    },
}));
const PageNameTag = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 20px',
    height: '100%',
    cursor: 'pointer',
    position: 'relative',
    borderLeft: '2px solid rgba(0,0,0,0.13)',
    background: 'transparent',
    transition: 'background 0.2s ease',
    '&:hover': {
        background: '#0e1d3a',
        '& .page-dot': {
            backgroundColor: '#facc15',
        },
        '& .page-label': {
            color: '#fff',
        },
    },
    '&:active': {
        transform: 'scale(0.97)',
    },
}));

const MainContent = styled(Box)(({ theme, collapsed }) => ({
    flexGrow: 1,
    backgroundColor: '#f4f6f8',
    minHeight: '100vh',
    marginLeft: collapsed ? collapsedWidth : drawerWidth,
    [theme.breakpoints.down('md')]: {
        marginLeft: 0,
    },
    transition: theme.transitions.create('margin-left', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
}));

const InfoCard = styled(Paper)(({ theme }) => ({
    width: '290px',
    margin: '0 auto',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    backgroundColor: 'white',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
}));

const StyledDashboardButton = styled(Button)(({ theme }) => ({
    padding: '5px 10px',
    borderRadius: '7px',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    borderColor: theme.palette.grey[400],
    textTransform: 'uppercase',
    fontSize: '0.65rem',
    '& .MuiButton-startIcon': {
        marginRight: theme.spacing(0.5),
    },
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        borderColor: theme.palette.grey[500],
    },
}));

const PostLoadButton = styled(StyledDashboardButton)(({ theme }) => ({
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.primary.main,
    borderColor: theme.palette.warning.main,
    '&:hover': {
        backgroundColor: '#e69900',
        borderColor: '#e69900',
    },
}));

// --- Sidebar content extracted as a reusable component ---
function SidebarContent({
    currentPage,
    handleNavigation,
    openTools, handleToolsClick, toolsRef,
    openSupport, handleSupportClick, supportRef,
    openAccount, handleAccountClick, accountRef,
    unreadCount,
    setFeedbackOpen,
    onLogout,
    isRestricted,
    isCollapsed,
    onToggleCollapse,
}) {
    return (
        <>
            <SidebarHeader>
                {!isCollapsed && (
                    <Box
                        component="img"
                        src={SD_logo}
                        alt="ShipperDock"
                        sx={{ width: '180px', height: 'auto', borderRadius: '0px' }}
                    />
                )}
            </SidebarHeader>
                   <Box sx={{ 
              flexGrow: 1, 
              overflowY: 'auto', 
              overflowX: 'hidden',
              // Custom Professional Scrollbar
              '&::-webkit-scrollbar': { width: '5px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.45)', borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.45)' },
          }}>
                <List>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Dashboard'} onClick={() => handleNavigation('Dashboard')}>
                    <Tooltip title={isCollapsed ? "Dashboard" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><DashboardIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                {!isRestricted && (
                    <>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Post Loads'} onClick={() => handleNavigation('Post Loads')}>
                    <Tooltip title={isCollapsed ? "Post Loads" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><ListAltIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Post Loads" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Search Trucks'} onClick={() => handleNavigation('Search Trucks')}>
                    <Tooltip title={isCollapsed ? "Search Trucks" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><SearchIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Search Trucks" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Search Carriers'} onClick={() => handleNavigation('Search Carriers')}>
                    <Tooltip title={isCollapsed ? "Search Carriers" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><LocalShippingIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Search Carriers" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Search Shippers'} onClick={() => handleNavigation('Search Shippers')}>
                    <Tooltip title={isCollapsed ? "Search Shippers" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><BusinessIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Search Shippers" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Favorites'} onClick={() => handleNavigation('Favorites')}>
                    <Tooltip title={isCollapsed ? "Favorites" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><StarIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Favorites" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                {/* <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Load History'} onClick={() => handleNavigation('Load History')}>
                    <Tooltip title={isCollapsed ? "Load History" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><InboxIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Load History" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Directory'} onClick={() => handleNavigation('Directory')}>
                    <Tooltip title={isCollapsed ? "Directory" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><GroupsIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Directory" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem> */}
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Updates & Tips'} onClick={() => handleNavigation('Updates & Tips')}>
                    <Tooltip title={isCollapsed ? "Updates & Tips" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><TipsAndUpdatesIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Updates & Tips" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>
                <SidebarItem collapsed={isCollapsed} ref={toolsRef} button onClick={handleToolsClick}>
                    <Tooltip title={isCollapsed ? "Tools" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><BuildIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Tools" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                    {!isCollapsed && (openTools ? <ExpandLess sx={{ fontSize: '1.1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1.1rem', opacity: 0.7 }} />)}
                </SidebarItem>
                <StyledCollapse in={openTools} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <NestedSidebarItem button onClick={() => handleNavigation('Templates')}>
                            <ListItemIcon><DescriptionIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Templates" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                        <NestedSidebarItem button onClick={() => handleNavigation('Market Map')}>
                            <ListItemIcon><DescriptionIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Market Map" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                    </List>
                </StyledCollapse>
                    </>
                )}
            </List>

            <Box sx={{ flexGrow: 1 }} />
            <Divider sx={{ backgroundColor: 'white', my: 1 }} />

            <List sx={{ p: 1 }}>
                <SidebarItem collapsed={isCollapsed} button selected={currentPage === 'Notifications'} onClick={() => handleNavigation('Notifications')}>
                    <Tooltip title={isCollapsed ? "Notifications" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}>
                            <Badge color="error" badgeContent={unreadCount}>
                                <NotificationsIcon sx={{ color: 'white' }} />
                            </Badge>
                        </ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Notifications" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                </SidebarItem>

                <SidebarItem collapsed={isCollapsed} ref={supportRef} button onClick={handleSupportClick}>
                    <Tooltip title={isCollapsed ? "Support" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><SupportIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="Support" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                    {!isCollapsed && (openSupport ? <ExpandLess sx={{ fontSize: '1.1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1.1rem', opacity: 0.7 }} />)}
                </SidebarItem>
                <StyledCollapse in={openSupport} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <NestedSidebarItem button onClick={() => handleNavigation('Community Forum')}>
                            <ListItemIcon><HelpOutlineIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Community Forum" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                        <NestedSidebarItem button onClick={() => handleNavigation('Help Center')}>
                            <ListItemIcon><InfoIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Help Center" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                        <NestedSidebarItem button onClick={() => setFeedbackOpen(true)}>
                            <ListItemIcon><SendIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Send Feedback" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                    </List>
                </StyledCollapse>

                <SidebarItem collapsed={isCollapsed} ref={accountRef} button onClick={handleAccountClick}>
                    <Tooltip title={isCollapsed ? "Account" : ""} placement="right">
                        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40 }}><AccountCircleIcon sx={{ color: 'white' }} /></ListItemIcon>
                    </Tooltip>
                    {!isCollapsed && <ListItemText primary="My Account" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 'bold' }} />}
                    {!isCollapsed && (openAccount ? <ExpandLess sx={{ fontSize: '1.1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1.1rem', opacity: 0.7 }} />)}
                </SidebarItem>
                <StyledCollapse in={openAccount} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <NestedSidebarItem button onClick={() => handleNavigation('Account Information')}>
                            <ListItemIcon><AccountBoxIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Account Information" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                        <NestedSidebarItem button onClick={() => handleNavigation('Privacy Policy')}>
                            <ListItemIcon><PolicyIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Privacy Policy" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                        <NestedSidebarItem button onClick={() => handleNavigation('Terms and Conditions')}>
                            <ListItemIcon><DescriptionIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Terms & Conditions" primaryTypographyProps={{ fontSize: '0.7rem' }} />
                        </NestedSidebarItem>
                        <NestedSidebarItem button onClick={onLogout}>
                            <ListItemIcon><LogoutIcon sx={{ color: 'white' }} /></ListItemIcon>
                            <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: '0.7rem', fontWeight: 'bold' }} />
                        </NestedSidebarItem>
                    </List>
                </StyledCollapse>
            </List>
            </Box>
            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
            <Box sx={{ display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-end', p: 1 }}>
                <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"} placement="right">
                    <IconButton onClick={onToggleCollapse} sx={{ color: 'white' }}>
                        {isCollapsed ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ChevronRightIcon sx={{ fontSize: '1.2rem' }} />
                                <ChevronRightIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                                <ChevronRightIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ChevronLeftIcon sx={{ fontSize: '1.2rem' }} />
                                <ChevronLeftIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                                <ChevronLeftIcon sx={{ fontSize: '1.2rem', ml: -1.5, color: 'grey.500' }} />
                            </Box>
                        )}
                    </IconButton>
                </Tooltip>
            </Box>
        </>
    );
}

// --- Main Dashboard ---
export default function BrokerDashboard({ onLogout }) {
    const navigate = useNavigate();
    const muiTheme = useTheme();

    // ─── MOBILE RESPONSIVE STATE ───
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

    const toolsRef = useRef(null);
    const supportRef = useRef(null);
    const accountRef = useRef(null);
    const [openSupport, setOpenSupport] = useState(false);
    const [openAccount, setOpenAccount] = useState(false);
    const [openTools, setOpenTools] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return sessionStorage.getItem('broker_sidebarCollapsed') === 'true';
    });
    const [currentPage, setCurrentPage] = useState('Dashboard');
    const [currentUser, setCurrentUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
    const [unreadCount, setUnreadCount] = useState(0);
    const [dealDeskUnread, setDealDeskUnread] = useState(0);
    const [previousPage, setPreviousPage] = useState(null);
    const [approvalFinished, setApprovalFinished] = useState(false);
    const [dealDeskOpen, setDealDeskOpen] = useState(false);
    const [dealDeskTruck, setDealDeskTruck] = useState(null);
    const [allSearchTrucks, setAllSearchTrucks] = useState([]);

    useEffect(() => {
        sessionStorage.setItem('broker_sidebarCollapsed', isCollapsed);
    }, [isCollapsed]);

    useEffect(() => {
        const role = localStorage.getItem('role');
        if (!role) {
            navigate('/');
        } else if (role !== 'broker') {
            navigate(`/dashboard/${role}`);
        }
    }, [navigate]);

    const fetchUnreadCount = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.get(`${API_BASE_URL}/api/notifications/unread-count`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadCount(res.data.count);
        } catch (err) {
            console.error("Failed to fetch unread notification count.", err);
        }
    };

    const fetchDealDeskUnread = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.get(`${API_BASE_URL}/api/deal-desk/unread-count`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDealDeskUnread(res.data.count || 0);
        } catch (err) {
            console.error("Failed to fetch Deal Desk unread count", err);
        }
    };

    const fetchUserStatus = async () => {
        const token = localStorage.getItem('jwtToken');
        if (token) {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const freshUser = res.data;
                setCurrentUser(freshUser);
                localStorage.setItem('user', JSON.stringify(freshUser));
            } catch (err) {
                console.error("Failed to refresh user status", err);
            }
        }
    };

    const sendHeartbeat = async () => {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;
        try {
            await axios.post(`${API_BASE_URL}/api/auth/heartbeat`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (err) {
            // Silent fail
        }
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setCurrentUser(JSON.parse(storedUser));
        }
        fetchUserStatus().finally(() => setLoadingUser(false));
        fetchUnreadCount();
        fetchDealDeskUnread();
        sendHeartbeat();

        const heartbeatInterval = setInterval(sendHeartbeat, 120000);
        const ddInterval = setInterval(fetchDealDeskUnread, 10000);
        const statusInterval = setInterval(fetchUserStatus, 30000);
        return () => { 
            clearInterval(ddInterval); 
            clearInterval(statusInterval); 
            clearInterval(heartbeatInterval);
        };
    }, []);

    if (loadingUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const handleSmoothScroll = (ref) => {
        setTimeout(() => {
            if (ref.current) {
                ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }, 300);
    };

    const handleToolsClick = () => {
        if (isCollapsed) return;
        const isOpening = !openTools;
        setOpenTools(isOpening);
        if (isOpening) {
            setOpenSupport(false);
            setOpenAccount(false);
            handleSmoothScroll(toolsRef);
        }
    };

    const handleSupportClick = () => {
        if (isCollapsed) return;
        const isOpening = !openSupport;
        setOpenSupport(isOpening);
        if (isOpening) {
            setOpenTools(false);
            setOpenAccount(false);
            handleSmoothScroll(supportRef);
        }
    };

    const handleAccountClick = () => {
        if (isCollapsed) return;
        const isOpening = !openAccount;
        setOpenAccount(isOpening);
        if (isOpening) {
            setOpenTools(false);
            setOpenSupport(false);
            handleSmoothScroll(accountRef);
        }
    };

    const handleNavigation = (page, source = null) => {
        if (isMobile) setMobileOpen(false);
        if (currentPage === page) {
            setRefreshKey(prev => prev + 1);
        } else {
            setCurrentPage(page);
            setPreviousPage(source);
            setRefreshKey(0);
        }
    };

    const showRightSidebar = currentPage === 'Dashboard';
    const showHeader = true;

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const sidebarProps = {
        currentPage,
        handleNavigation,
        openTools, handleToolsClick, toolsRef,
        openSupport, handleSupportClick, supportRef,
        openAccount, handleAccountClick, accountRef,
        unreadCount,
        setFeedbackOpen,
        onLogout,
        isRestricted: currentUser?.admin_approval_status !== 'approved',
        isCollapsed,
        onToggleCollapse: () => setIsCollapsed(!isCollapsed),
    };

    const isRestricted = currentUser?.admin_approval_status !== 'approved';

    const renderPage = () => {
        const showPending = isRestricted &&
            !['Community Forum', 'Help Center', 'Notifications', 'Account Information'].includes(currentPage);

        if (showPending) {
            return <PendingApproval user={currentUser} onComplete={() => setApprovalFinished(true)} />;
        }

        switch (currentPage) {
            case 'Dashboard':
                return (
                    <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: 2 }}>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={12} sm={6} md={4}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: { xs: '100%', sm: '290px' } }}>
                                    <InfoIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Welcome to the ShipperDock
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Learn more about our platform and mission.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleNavigation('Company Profile')}
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            fontWeight: 'bold',
                                            borderRadius: '7px',
                                            px: 3,
                                            '&:hover': { backgroundColor: theme.palette.secondary.main },
                                        }}
                                    >
                                        View Company Profile
                                    </Button>
                                </InfoCard>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: { xs: '100%', sm: '290px' } }}>
                                    <NotificationsIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Updates & Tips
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Discover new features and helpful tips.
                                    </Typography>
                                    <Button
                                        variant="contained" onClick={() => handleNavigation('Updates & Tips')}
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            fontWeight: 'bold',
                                            borderRadius: '7px',
                                            px: 3,
                                            '&:hover': { backgroundColor: theme.palette.secondary.main },
                                        }}
                                    >
                                        View Updates
                                    </Button>
                                </InfoCard>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: { xs: '100%', sm: '290px' } }}>
                                    <CalendarMonthIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Training Resources
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Stay up-to-date with our video tutorials
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleNavigation('Training')}
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            fontWeight: 'bold',
                                            borderRadius: '7px',
                                            px: 3,
                                            '&:hover': { backgroundColor: theme.palette.secondary.main },
                                        }}
                                    >
                                        Watch Videos
                                    </Button>
                                </InfoCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: { xs: '100%', sm: '460px' } }}>
                                    <HelpOutlineIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        ShipperDock Community Support
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Ask questions, share experiences, and get solutions from the ShipperDock user community.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleNavigation('Community Forum')}
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            fontWeight: 'bold',
                                            borderRadius: '7px',
                                            px: 3,
                                            '&:hover': { backgroundColor: theme.palette.secondary.main },
                                        }}
                                    >
                                        Community Forum
                                    </Button>
                                </InfoCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoCard sx={{ textAlign: 'center', alignItems: 'center', width: { xs: '100%', sm: '460px' } }}>
                                    <FeedbackIcon sx={{ fontSize: 30, color: theme.palette.warning.main, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Feedback
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Help us improve the platform. Your feedback is valuable.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => setFeedbackOpen(true)}
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            fontWeight: 'bold',
                                            borderRadius: '7px',
                                            px: 3,
                                            '&:hover': { backgroundColor: theme.palette.secondary.main },
                                        }}
                                    >
                                        GIVE FEEDBACK
                                    </Button>
                                </InfoCard>
                            </Grid>
                        </Grid>
                    </Container>
                );
            case 'Post Loads':
                return <PostLoads key={refreshKey} />;
            case 'Search Trucks':
                return <SearchTrucks
                    key={refreshKey}
                    onOpenDealDesk={(truck) => {
                        setDealDeskTruck(truck);
                        setDealDeskOpen(true);
                    }}
                    onSearchResultsChange={setAllSearchTrucks}
                />;
            case 'Company Profile':
                return <CompanyProfile />;
            case 'Load History':
                return <LoadHistory key={refreshKey} />;
            case 'Directory':
                return <Directory key={refreshKey} />;
            case 'Search Carriers':
                return <SearchCarriers key={refreshKey} />;
            case 'Search Shippers':
                return <SearchShippers key={refreshKey} />;
            case 'Favorites':
                return <FavoritesViewer key={refreshKey} userRole="broker" />;
                case 'Templates':
                return <TemplatesViewer key={refreshKey} />;
            case 'Updates & Tips':
                return <UpdatesAndTipsViewer key={refreshKey} />;
            case 'Training':
                return <TrainingViewer key={refreshKey} />;
            case 'Notifications':
                return <Notifications key={refreshKey} onNotificationsRead={() => setUnreadCount(0)} />;
            case 'Account Information':
                return <UserProfile key={refreshKey} />;
            case 'Market Map':
                return <MarketMap key={refreshKey} />;
            case 'Privacy Policy':
                return <PrivacyPolicy key={refreshKey} />;
            case 'Terms and Conditions':
                return <TermsAndConditions key={refreshKey} onNavigate={handleNavigation} />;
            case 'Community Forum':
                return <SupportForum key={refreshKey} currentUser={currentUser} onNavigate={handleNavigation} />;
            case 'Help Center':
                return <HelpCenter key={refreshKey} onNavigate={handleNavigation} previousPage={previousPage} />;
            default:
                return null;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <StyledBox>

                {/* ─── DESKTOP SIDEBAR (hidden on mobile) ─── */}
                {!isMobile && (
                    <Sidebar collapsed={isCollapsed}>
                        <SidebarContent {...sidebarProps} />
                    </Sidebar>
                )}

                {/* ─── MOBILE DRAWER (hidden on desktop) ─── */}
                {isMobile && (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                background: 'linear-gradient(to bottom, #0e1d3a, #11224d)',
                                color: 'white',
                                boxSizing: 'border-box',
                                overflowY: 'auto',
                            },
                        }}
                    >
                        <SidebarContent {...sidebarProps} />
                    </Drawer>
                )}

               {/* ─── HEADER (Post Loads / Search Trucks) ─── */}
{showHeader && (
    <DashboardHeader
        collapsed={isCollapsed}
        sx={{
            left: isMobile ? 0 : (isCollapsed ? collapsedWidth : drawerWidth),
            right: 0,
            width: isMobile ? '100%' : `calc(100% - ${isCollapsed ? collapsedWidth : drawerWidth}px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between', // Pushes Nav to left, Logout to right
            paddingX: 0,
        }}
    >
        {/* LEFT SECTION: NAVIGATION */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%', ml: 0 }}>
            {isMobile && (
                <Box
                    onClick={handleDrawerToggle}
                    sx={{
                        display: 'flex', alignItems: 'center', px: '12px',
                        cursor: 'pointer',
                        borderRight: '2px solid rgba(0,0,0,0.1)',
                        color: 'rgba(0,0,0,0.7)',
                        transition: 'all 0.2s',
                        '&:hover': { background: '#0e1d3a', color: '#fff' },
                    }}
                >
                    <Badge color="error" badgeContent={unreadCount} invisible={currentPage === 'Notifications'}>
                        <MenuIcon sx={{ fontSize: '1.1rem' }} />
                    </Badge>
                </Box>
            )}

            <NavTab
                isFirst
                active={currentPage === 'Post Loads' ? 1 : 0}
                onClick={() => handleNavigation('Post Loads')}
            >
                <ListAltIcon sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
                {!isMobile && 'Post a Load'}
            </NavTab>

            <NavTab
                active={currentPage === 'Search Trucks' ? 1 : 0}
                onClick={() => handleNavigation('Search Trucks')}
            >
                <SearchIcon />
                {!isMobile && 'Search Trucks'}
            </NavTab>

            <Badge
                badgeContent={dealDeskUnread}
                color="error"
                sx={{ '& .MuiBadge-badge': { top: 8, right: 6 }, display: 'flex' }}
            >
                <NavTab
                    isDealDesk
                    active={dealDeskOpen ? 1 : 0}
                    onClick={() => setDealDeskOpen(!dealDeskOpen)}
                >
                    <PermContactCalendarIcon />
                    {!isMobile && 'DealDesk'}
                </NavTab>
            </Badge>
        </Box>

        {/* RIGHT SECTION: PAGE NAME & RED LOGOUT */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
            <PageNameTag>
                <Box
                    className="page-dot"
                    sx={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        backgroundColor: '#0e1d3a', flexShrink: 0,
                        transition: 'background 0.2s ease',
                    }}
                />
                <Typography
                    className="page-label"
                    sx={{
                        fontSize: '0.68rem', fontWeight: 800,
                        color: 'rgba(0,0,0,0.8)', textTransform: 'uppercase',
                        letterSpacing: '0.9px', transition: 'color 0.2s ease',
                    }}
                >
                    {currentPage}
                </Typography>
            </PageNameTag>

            {/* LOGOUT ICON PINNED TO END */}
            <NavTab
                active={0}
                onClick={onLogout}
                sx={{
                    color: '#ffffff', // Red color
                    borderLeft: '2px solid rgba(0,0,0,0.13)', // Divider on the left
                    borderRight: 'none',
                    '& .MuiSvgIcon-root': { color: '#ffffff' },
                    '&:hover': {
                        background: '#dc2626',
                        color: '#ffffff',
                        '& .MuiSvgIcon-root': { color: '#ffffff' },
                    },
                }}
            >
                <LogoutIcon />
                {!isMobile && 'Logout'}
            </NavTab>
        </Box>
    </DashboardHeader>
)}
                {/* ─── MAIN CONTENT ─── */}
                <MainContent
                    collapsed={isCollapsed}
                    hasRightSidebar={false}
                    showHeader={showHeader}
                    sx={{
                        paddingTop: currentPage === 'Dashboard'
                            ? `calc(${dashboardHeaderHeight} + 24px)`
                            : `calc(${dashboardHeaderHeight} + -4px)`,
                        paddingX: 0,
                        paddingBottom: 0,
                        backgroundColor: '#f4f6f8',
                        minHeight: '100vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    {renderPage()}
                </MainContent>
            </StyledBox>

            {/* Floating Live Chat - only visible on Support pages */}
            {['Community Forum', 'Help Center'].includes(currentPage) && (
                <LiveChat />
            )}

            {/* ✅ Snackbar for success message */}alk
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Feedback Dialog */}
            <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

            {/* Deal Desk */}
            {dealDeskOpen && (
                <DealDesk
                    loadOrTruckId={dealDeskTruck?.id}
                    type={dealDeskTruck?._itemType || 'truck'}
                    loadData={dealDeskTruck}
                    userData={currentUser}
                    allLoads={[]}
                    allTrucks={allSearchTrucks}
                    onSelectLoad={(load) => setDealDeskTruck({ ...load, _itemType: 'load' })}
                    onLogout={onLogout}
                    onSelectTruck={(truck) => setDealDeskTruck({ ...truck, _itemType: 'truck' })}
                    onBack={() => {
                        setDealDeskOpen(false);
                        setDealDeskTruck(null);
                    }}
                />
            )}
        </ThemeProvider>
    );
}